<template>
  <form :class="$style.form">
    <form-item :title="'Пресет видимости'">
      <select-input
        :value="value.preset_id"
        :options="presetOptions"
        :loading="presetsLoading"
        :placeholder="'Выберите пресет'"
        bind-value-id
        @input="updateValue('preset_id', $event)"
      />
    </form-item>

    <form-item :title="'Метки'">
      <div :class="$style.labelsInput">
        <div
          v-for="(label, index) in labelsList"
          :key="index"
          :class="$style.labelInput"
        >
          <text-input
            :value="label"
            :placeholder="'Введите название метки'"
            @input="updateLabel(index, $event)"
          />
          <icon
            name="trash"
            :class="$style.removeIcon"
            button
            @click="removeLabel(index)"
          />
        </div>
        
        <btn
          type="button"
          secondary
          :class="$style.addLabelBtn"
          @click="addLabel"
        >
          <icon name="plus" :class="$style.addIcon" />
          Добавить метку
        </btn>
      </div>
    </form-item>

    <div v-if="error" :class="$style.error">
      {{ error }}
    </div>
  </form>
</template>

<script>
import { mapActions } from 'vuex';
import FormItem from '@/ui-kit/FormItem';
import SelectInput from '@/ui-kit/SelectInput';
import TextInput from '@/ui-kit/TextInput';
import Btn from '@/ui-kit/Btn';
import Icon from '@/ui-kit/Icon';

export default {
  name: 'Top100LabelsForm',
  components: {
    FormItem,
    SelectInput,
    TextInput,
    Btn,
    Icon,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    presets: {
      type: Array,
      default: () => [],
    },
    presetsLoading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      labelsList: [],
    };
  },
  computed: {
    presetOptions() {
      return this.presets.map(preset => ({
        id: preset.id,
        title: this.getPresetName(preset.id)
      }));
    },
  },
  watch: {
    value: {
      handler(newValue) {
        if (newValue && newValue.labels_list) {
          this.labelsList = [...newValue.labels_list];
        } else {
          this.labelsList = [''];
        }
      },
      immediate: true,
    },
    
    // Отслеживаем изменение preset_id для загрузки существующих меток
    'value.preset_id': {
      handler(newPresetId, oldPresetId) {
        if (newPresetId && newPresetId !== oldPresetId) {
          this.loadExistingLabels(newPresetId);
        }
      },
    },
  },
  methods: {
    ...mapActions('top100Labels', [
      'fetchLabels',
    ]),
    
    getPresetName(presetId) {
      const preset = this.presets.find(p => p.id === presetId);
      if (!preset) return `Пресет #${presetId}`;
      
      // Формируем название пресета из доступных данных
      const searchEngine = preset.search_engine === 'yandex' ? 'Яндекс' : 'Google';
      const device = preset.device === 'desktop' ? 'Десктоп' : 'Мобильный';
      const region = preset.pos_region || 'Неизвестно';
      
      return `${region}, ${device} - ${searchEngine}`;
    },
    
    async loadExistingLabels(presetId) {
      try {
        // Загружаем метки для выбранного пресета
        const response = await this.fetchLabels({ 
          projectId: this.projectId, 
          presetId: presetId 
        });
        
        // Ищем метки для выбранного пресета
        const existingLabels = response.find(label => label.preset_id === presetId);
        
        if (existingLabels && existingLabels.labels_list && existingLabels.labels_list.length > 0) {
          // Если есть существующие метки, загружаем их
          this.labelsList = [...existingLabels.labels_list];
          this.updateLabelsList();
          
          // Обновляем formValue с существующими данными
          this.$emit('input', {
            ...this.value,
            id: existingLabels.id,
            labels_list: existingLabels.labels_list,
          });
        } else {
          // Если меток нет, оставляем пустое поле
          this.labelsList = [''];
          this.updateLabelsList();
          
          // Сбрасываем id, так как это новая запись
          this.$emit('input', {
            ...this.value,
            id: undefined,
            labels_list: [],
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки существующих меток:', error);
        // В случае ошибки оставляем пустое поле
        this.labelsList = [''];
        this.updateLabelsList();
        
        // Сбрасываем id, так как это новая запись
        this.$emit('input', {
          ...this.value,
          id: undefined,
          labels_list: [],
        });
      }
    },

    updateValue(field, value) {
      this.$emit('input', {
        ...this.value,
        [field]: value,
      });
    },

    updateLabel(index, value) {
      const newLabels = [...this.labelsList];
      newLabels[index] = value;
      this.labelsList = newLabels;
      this.updateLabelsList();
    },

    addLabel() {
      this.labelsList.push('');
    },

    removeLabel(index) {
      if (this.labelsList.length > 1) {
        const newLabels = [...this.labelsList];
        newLabels.splice(index, 1);
        this.labelsList = newLabels;
        this.updateLabelsList();
      }
    },

    updateLabelsList() {
      // Фильтруем пустые метки
      const filteredLabels = this.labelsList.filter(label => label.trim() !== '');
      
      this.$emit('input', {
        ...this.value,
        labels_list: filteredLabels,
      });
    },

    validate() {
      const errors = [];

      if (!this.value.preset_id) {
        errors.push('Выберите пресет видимости');
      }

      const validLabels = this.labelsList.filter(label => label.trim() !== '');
      if (validLabels.length === 0) {
        errors.push('Добавьте хотя бы одну метку');
      }

      // Проверяем длину меток
      for (const label of validLabels) {
        if (label.length > 255) {
          errors.push('Метка не должна превышать 255 символов');
          break;
        }
      }

      return errors;
    },
  },
};
</script>

<style lang="scss" module>
@import "~@/ui-kit/_theme/main.scss";

.form {
  display: flex;
  flex-direction: column;
  gap: $gap-m;
}


.labelsInput {
  display: flex;
  flex-direction: column;
  gap: $gap-s;
}

.labelInput {
  display: flex;
  align-items: center;
  gap: $gap-s;
}

.removeIcon {
  color: $text-secondary-color;
  flex-shrink: 0;
  
  &:hover {
    color: $danger-color;
  }
}

.addLabelBtn {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: $gap-xs;
}

.addIcon {
  width: 16px;
  height: 16px;
}

.error {
  color: $danger-color;
  font-size: 14px;
  padding: $gap-s;
  background: rgba($danger-color, 0.1);
  border-radius: $border-radius-block;
  border: 1px solid rgba($danger-color, 0.2);
}
</style>
